import React, { createContext, Suspense, useContext, useState } from "react";
import { v4 as uuid } from "uuid";
import { BaseDialog } from "./base-dialog";

export type DialogProps = {
  id: string;
  component: React.ComponentType<any>;
  props?: any;
  containerProps?: any;
  showFloatingCloseButton?: boolean;
  closePromise: Promise<any>;
  closeResolve: (result: any) => void;
};

/**
 * Create a DialogProvider that allows child components
 * to open a dialog that is resolved via a promise.
 * @param BaseDialogComponent
 */
function CreateDialogProvider(BaseDialogComponent = BaseDialog) {
  const Context = createContext({
    dialogs: [] as DialogProps[],
    open: async () => null,
    close: () => null,
  } as {
    dialogs: DialogProps[];
    open: (dialog: Omit<DialogProps, "id" | "closePromise" | "closeResolve">) => Promise<any>;
    close: (id: string, cancel: boolean, result: any) => void;
  });

  const { Provider } = Context;

  const DialogProvider = ({ children }: any) => {
    const [dialogs, setDialogs] = useState<DialogProps[]>([]);

    /**
     * Open the dialog that was passed in the format of
     * {
     * component: React Component
     * props: Props for the react component
     * }
     * @param newDialog
     */
    const open = (newDialog: Omit<DialogProps, "id" | "closePromise" | "closeResolve">): Promise<any> => {
      if (!newDialog || !newDialog.component) {
        throw new Error("Invalid Dialog");
      }

      let closeResolve = (res: any) => {};
      const closePromise = new Promise((resolve) => {
        closeResolve = resolve;
      });

      const dialog: Partial<DialogProps> = {
        id: uuid(),
        ...newDialog,
        props: {
          ...(newDialog.props || {}),
        },
        containerProps: {
          ...(newDialog.containerProps || { maxWidth: "sm", p: 2 }),
          open: true,
        },
        closeResolve,
        // Configure the promise, so that the dialog can return the results
        closePromise,
      };

      setDialogs((curr) => [...curr, dialog as DialogProps]);

      return closePromise as Promise<any>;
    };

    /**
     * Close the dialog
     * @param id The ID of the dialog to close
     * @param cancel Was it cancelled or successful
     * @param result The result of the dialog
     */
    const close = (id: string, cancel: boolean, result: any) => {
      const dialog = dialogs.find((d) => d.id === id);

      if (!dialog) return;

      // Resolve the promise
      dialog.closeResolve({ wasCancelled: cancel, result });

      setDialogs((curr) =>
        curr.map((d) => {
          if (d.id === id) {
            d.containerProps.open = false;
          }
          return d;
        }),
      );

      setTimeout(() => {
        // After the animation is complete, set the dialog to null
        setDialogs((prev) => prev.filter((d) => d.id !== id));
      }, 300);
    };

    const DialogContainer = (
      <>
        {dialogs.map((dialog) => {
          const DialogComponent = dialog.component;
          const props = dialog.props || {};
          const containerProps = dialog.containerProps || {};
          const { showFloatingCloseButton } = dialog;

          return (
            <BaseDialogComponent
              key={dialog.id}
              cancel={(result: any) => close(dialog.id, true, result)}
              showFloatingCloseButton={showFloatingCloseButton}
              onBackdropClick={() => close(dialog.id, true, null)}
              {...containerProps}
            >
              <DialogComponent
                ok={(result: any) => close(dialog.id, false, result)}
                cancel={(result: any) => close(dialog.id, true, result)}
                {...props}
              />
            </BaseDialogComponent>
          );
        })}
      </>
    );

    return (
      <Provider value={{ dialogs, open, close }}>
        {/* Allow the Lazy Loading of Dialogs */}
        <Suspense fallback={null}>
          {DialogContainer}
          {children}
        </Suspense>
      </Provider>
    );
  };

  const useDialog = () => useContext(Context);

  return {
    DialogProvider,
    useDialog,
  };
}

const { DialogProvider, useDialog } = CreateDialogProvider();

export { DialogProvider, useDialog };
